import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

/** Router state attached to route rewrites made by the scroll spy. */
export const DOC_SCROLL_SPY_STATE = { docScrollSpy: true } as const

export function isDocScrollSpyNavigation(state: unknown): boolean {
  return (
    typeof state === 'object' &&
    state !== null &&
    (state as Record<string, unknown>).docScrollSpy === true
  )
}

/** DOM id of a documentation section on a continuous page. */
export function docSectionId(slug: string): string {
  return `dp-doc-section-${slug}`
}

// A section is "current" once its top has passed the top 30% of the page area.
const SPY_THRESHOLD = 0.3
// Sub-pixel rounding keeps scrollTop a fraction short of the very bottom.
const BOTTOM_TOLERANCE = 2

export interface SectionPosition {
  slug: string
  /** Viewport y of the section's top edge (`getBoundingClientRect().top`). */
  top: number
}

/**
 * The section the reader is on: the last one whose top has passed the top
 * 30% of the visible area. At the very bottom of the page the last sections
 * can never reach that line (there is nothing left to scroll), so there the
 * last section that is visible at all wins.
 */
export function pickCurrentSection(
  sections: SectionPosition[],
  view: { top: number; bottom: number },
  atBottom: boolean
): string | undefined {
  if (sections.length === 0) return undefined
  if (atBottom) {
    return [...sections].reverse().find(section => section.top < view.bottom)?.slug
  }
  const line = view.top + (view.bottom - view.top) * SPY_THRESHOLD
  return [...sections].reverse().find(section => section.top <= line)?.slug ?? sections[0].slug
}

/** Nearest ancestor that scrolls vertically; the workspace's content area, else the window. */
function scrollParent(element: HTMLElement): HTMLElement | null {
  for (let node = element.parentElement; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node)
    if (overflowY === 'auto' || overflowY === 'scroll') return node
  }
  return null
}

const SCROLL_KEYS = new Set(['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End', ' '])

/**
 * Keeps a continuously scrolling documentation page and its `:slug` route in
 * step (design review SAIV-11493): a route change scrolls the matching
 * section to the top of the page area, and scrolling past a section
 * rewrites the route in place so the sidebar and breadcrumb follow. Spy
 * rewrites carry `DOC_SCROLL_SPY_STATE` so page-view tracking can skip them.
 *
 * The spy only follows scrolling the reader does (wheel, touch, keyboard,
 * scrollbar). The scroll a sidebar click triggers never rewrites the route:
 * a short last section cannot reach the top of the page, and the spy used to
 * replace it with the section above, so the click looked like it did nothing.
 */
export function useDocSectionScroll(
  activeSlug: string | undefined,
  slugs: readonly string[],
  pathFor: (slug: string) => string
): void {
  const navigate = useNavigate()
  const activeRef = useRef(activeSlug)
  activeRef.current = activeSlug
  // Set by the spy before it rewrites the route; the scroll effect then
  // leaves the user's scroll position alone.
  const spyNavigated = useRef(false)
  // Whether the reader has scrolled since the last route-driven scroll.
  const readerScrolled = useRef(false)

  // Route → scroll.
  useEffect(() => {
    if (!activeSlug) return
    if (spyNavigated.current) {
      spyNavigated.current = false
      return
    }
    readerScrolled.current = false
    const element = document.getElementById(docSectionId(activeSlug))
    if (element && typeof element.scrollIntoView === 'function') {
      element.scrollIntoView({ block: 'start' })
    }
  }, [activeSlug])

  // Scroll → route.
  useEffect(() => {
    const first = slugs.map(slug => document.getElementById(docSectionId(slug))).find(Boolean)
    if (!first) return
    const container = scrollParent(first)
    const target: HTMLElement | Window = container ?? window

    const markReaderScroll = () => {
      readerScrolled.current = true
    }
    const markKeyScroll = (event: KeyboardEvent) => {
      if (SCROLL_KEYS.has(event.key)) readerScrolled.current = true
    }

    let frame = 0
    const sync = () => {
      frame = 0
      if (!readerScrolled.current) return
      const sections = slugs
        .map(slug => ({ slug, element: document.getElementById(docSectionId(slug)) }))
        .filter((entry): entry is { slug: string; element: HTMLElement } => entry.element !== null)
        .map(({ slug, element }) => ({ slug, top: element.getBoundingClientRect().top }))
      const view = container
        ? container.getBoundingClientRect()
        : { top: 0, bottom: window.innerHeight }
      const atBottom = container
        ? container.scrollTop + container.clientHeight >= container.scrollHeight - BOTTOM_TOLERANCE
        : window.scrollY + window.innerHeight >=
          document.documentElement.scrollHeight - BOTTOM_TOLERANCE
      const current = pickCurrentSection(sections, view, atBottom)
      if (!current || current === activeRef.current) return
      spyNavigated.current = true
      navigate(pathFor(current), { replace: true, state: DOC_SCROLL_SPY_STATE })
    }
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(sync)
    }

    target.addEventListener('scroll', onScroll, { passive: true })
    target.addEventListener('wheel', markReaderScroll, { passive: true })
    target.addEventListener('touchmove', markReaderScroll, { passive: true })
    // Dragging the scrollbar starts with a pointerdown on the container.
    target.addEventListener('pointerdown', markReaderScroll)
    window.addEventListener('keydown', markKeyScroll)
    return () => {
      if (frame) cancelAnimationFrame(frame)
      target.removeEventListener('scroll', onScroll)
      target.removeEventListener('wheel', markReaderScroll)
      target.removeEventListener('touchmove', markReaderScroll)
      target.removeEventListener('pointerdown', markReaderScroll)
      window.removeEventListener('keydown', markKeyScroll)
    }
  }, [slugs, pathFor, navigate])
}
